import {
  ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { filter, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  AppliedToplines, CampaignToplineSettingItemTypes, GetEntityById2, GetEntityById2GQL,
  GetEntityConnectionTypeGQL, GetTagCategoryByIdGQL, NoteTarget
} from 'generated/graphql';

import {
  ENTITY_CONNECTIONS_CHANGED
} from 'app/_state/entity/view-entity/entity-connections/entity-connection.actions';
import { ToplineAddClickedEvent } from 'app/toplines/components/ToplineAddClickedEvent';
import { extractToplines } from 'app/toplines/helpers/extract-toplines';
import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import {
  AddConnectionDialog, AddConnectionDialogData
} from 'entity/dialogs/add-connection/add-connection.dialog';
import {
  AddOrUpdateAddressDialog, AddOrUpdateAddressDialogData
} from 'entity/dialogs/add-or-update-address/add-or-update-address.dialog';
import {
  AddOrUpdateEmailDialog, AddOrUpdateEmailDialogData
} from 'entity/dialogs/add-or-update-email/add-or-update-email.dialog';
import {
  AddOrUpdatePhoneDialog, AddOrUpdatePhoneDialogData
} from 'entity/dialogs/add-or-update-phone/add-or-update-phone.dialog';
import {
  ApplyTagDialog, ApplyTagDialogData
} from 'field-shared/dialogs/apply-tag/apply-tag.dialog';
import { getSafe, isNilOrEmpty } from 'helpers';
import { AddOrUpdateSocialDialog, AddOrUpdateSocialDialogData } from 'entity/dialogs/add-or-update-social/add-or-update-social.dialog';
import { SocialNetworksService } from 'app/social/get-social-networks.service';
import { NoteDialogComponent } from 'app/note/dialogs/note-dialog/note-dialog.component';
import { AddOrUpdateLanguageDialog, AddOrUpdateLanguageDialogData } from 'entity/dialogs/add-or-update-language/add-or-update-language.dialog';
import { AddOrUpdateDOBDialog, AddOrUpdateDOBDialogData } from 'entity/dialogs/add-or-update-dob/add-or-update-dob.dialog';

@Component({
  selector: 'app-entity-toplines',
  templateUrl: './entity-toplines.component.html'
})
export class EntityToplinesComponent extends WithDisposableSubject(WithChangesSubject(class { }))
  implements OnInit, OnDestroy, OnChanges {

  @Input() entityId: string;
  @Input() campaignId: string;

  entity: GetEntityById2.GetEntity;
  extractedToplines: Array<{ toplineItem: any; appliedToplineValue: any }> = [];

  constructor(
    private _getEntityGQL: GetEntityById2GQL,
    private _getEntityConnectionType: GetEntityConnectionTypeGQL,
    private _getTagCategoryGQL: GetTagCategoryByIdGQL,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _actions$: Actions
  ) {
    super();
  }

  // Lifecycle hooks
  ngOnInit() {
    this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith({ campaignId: this.campaignId, entityId: this.entityId }),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !isNilOrEmpty(campaignId) && !isNilOrEmpty(entityId)),
      switchMap(({ campaignId, entityId }) => this._getEntityGQL.watch({
        entityId, campaignId, withToplines: true,
      }, {
        fetchPolicy: 'cache-first'
      }).valueChanges.pipe(takeUntil(this.disposer))),
      map(({ data }) => data?.getEntity)
    ).subscribe((entity) => {
      this.entity = entity;
      this.extractedToplines = this._extractToplines(entity.appliedToplines as AppliedToplines);
      const hasConnectionToplines = this.extractedToplines.some(t =>
        t.toplineItem.itemType === CampaignToplineSettingItemTypes.EntityConnectionType
      );
      if (hasConnectionToplines) {
        this._watchForConnectionChanges();
      }

      this._cd.markForCheck();
    });
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    super.ngOnChanges(simpleChanges);
  }

  private _watchForConnectionChanges() {
    return this._actions$.pipe(
      ofType(ENTITY_CONNECTIONS_CHANGED)
    ).pipe(
      takeUntil(this.disposer),
      filter(({ payload }) => [
        payload?.entityConnection?.entity1?.id,
        payload?.entityConnection?.entity2?.id
      ].includes(this.entityId)),
      switchMap((_) => this._getEntityGQL.fetch(
        {
          entityId: this.entity.id,
          campaignId: this.campaignId,
          withToplines: true
        },
        { fetchPolicy: 'network-only' }))
    ).subscribe(() => {
      // Entity refteched
    });
  }

  private _getTagCategory(tagCategoryId: string) {
    return this._getTagCategoryGQL.fetch({
      campaignId: this.campaignId,
      tagCategoryId
    }).pipe(
      map(({ data }) => data.getTagCategoryById)
    );
  }

  private _extractToplines(appliedToplines: AppliedToplines) {
    return extractToplines(appliedToplines);
  }

  handleAddClicked(event: ToplineAddClickedEvent) {
    if (event?.topline) {
      this.openDialog(event);
    }
  }

  openDialog(event: ToplineAddClickedEvent) {
    const entity = this.entity;
    const topline = event.topline;

    switch (event.topline.toplineItem.itemType) {
      case CampaignToplineSettingItemTypes.PreferredLanguage:
        this._dialog
          .open<AddOrUpdateLanguageDialog, AddOrUpdateLanguageDialogData>(AddOrUpdateLanguageDialog, {
            data: {
              entityId: entity.id,
              campaignId: entity.campaignContextId,
            },
            disableClose: false
          });
        break;
      case CampaignToplineSettingItemTypes.SocialNetwork:
        const network = SocialNetworksService.getSocialNetworkDetails(event.topline.toplineItem.itemId);
        this._dialog
          .open<AddOrUpdateSocialDialog, AddOrUpdateSocialDialogData>(AddOrUpdateSocialDialog, {
            data: {
              entityId: entity.id,
              campaignId: entity.campaignContextId,
              isCreate: true,
              socialProfile: {
                socialNetwork: {
                  id: network.id,
                  label: network.label,
                }
              }
            },
            disableClose: false
          });
        break;
      case CampaignToplineSettingItemTypes.Email:
        this._dialog
          .open<AddOrUpdateEmailDialog, AddOrUpdateEmailDialogData>(AddOrUpdateEmailDialog, {
            data: {
              entityId: entity.id,
              campaignId: entity.campaignContextId,
              // new email
              email: null
            },
            disableClose: false
          });
        break;
      case CampaignToplineSettingItemTypes.EntityConnectionType:
        this._getEntityConnectionType.fetch({ id: topline?.toplineItem?.itemId })
          .pipe(
            map(({ data }) => data.getEntityConnectionType)
          ).subscribe((ect) => {
            const dialog = this._dialog
              .open<AddConnectionDialog, AddConnectionDialogData>(AddConnectionDialog, {
                data: {
                  fromEntityId: entity.id,
                  fromEntityTypeId: entity.entityTypeId,
                  campaignId: entity.campaignContextId,
                  entityConnectionType: ect
                },
                disableClose: true
              });
          });
        break;
      case CampaignToplineSettingItemTypes.PhoneNumber:
        this._dialog
          .open<AddOrUpdatePhoneDialog, AddOrUpdatePhoneDialogData>(AddOrUpdatePhoneDialog, {
            data: {
              entityId: entity.id,
              campaignId: entity.campaignContextId,
              // new phone
              phone: null
            },
            disableClose: false
          });
        break;
      case CampaignToplineSettingItemTypes.Address:
        this._dialog
          .open<AddOrUpdateAddressDialog, AddOrUpdateAddressDialogData>(AddOrUpdateAddressDialog, {
            data: {
              entityId: entity.id,
              campaignId: entity.campaignContextId,
              // new adddress
              address: null
            },
            // Disable close needed for address typehaead
            disableClose: true
          });
        break;
      case CampaignToplineSettingItemTypes.Age:
       AddOrUpdateDOBDialog.open(this._dialog,
          {
            data: {
              entityId: entity.id,
              campaignId: entity.campaignContextId
            }
          });
        break;
      case CampaignToplineSettingItemTypes.Note:
        NoteDialogComponent.open(this._dialog, {
          data: {
            campaignId: this.campaignId,
            targetId: this.entityId,
            targetType: NoteTarget.Entity,
            noteType: 'text',
            heading: 'Add Note',
            hideNotes: true
          }
        });
        break;
      case CampaignToplineSettingItemTypes.TagCategory:
        this._getTagCategory(topline.toplineItem.itemId)
          .subscribe(category => {
            this._dialog
              .open<ApplyTagDialog, ApplyTagDialogData>(
                ApplyTagDialog, {
                data: {
                  campaignId: this.campaignId,
                  targetType: event.targetType,
                  targetId: event.targetId,
                  category
                }
              }
              );
          });
        break;
    }
  }
}
